// The entity types this client tracks on the sync feed, with the field allowlist
// each one may write. Mirrors the server-side registry
// (backend/crates/application/src/registry.rs) — a field missing here is a field
// the web can never send.

import type {
  AccountInput,
  ActivityInput,
  AssetInput,
  BattlePlanInput,
  BattlePlanItemInput,
  CategoryInput,
  CommodityInput,
  EntityType,
  TagInput,
  EventInput,
  EventTypeInput,
  LongTermGoalInput,
  MetricInput,
  PersonInput,
  PlanInput,
  PointInput,
  ScheduleInput,
  TaskInput,
  TimelineInput,
  TransactionInput,
} from "@/types";

export const ENTITY_ACTIVITY: EntityType = "activity";
export const ENTITY_LONG_TERM_GOAL: EntityType = "longTermGoal";
export const ENTITY_PLAN: EntityType = "plan";
export const ENTITY_TASK: EntityType = "task";
export const ENTITY_METRIC: EntityType = "metric";
export const ENTITY_POINT: EntityType = "point";
export const ENTITY_TIMELINE: EntityType = "timeline";
export const ENTITY_EVENT: EntityType = "event";
export const ENTITY_PERSON: EntityType = "person";
export const ENTITY_COMMODITY: EntityType = "commodity";
export const ENTITY_CATEGORY: EntityType = "category";
export const ENTITY_TAG: EntityType = "tag";
export const ENTITY_ACCOUNT: EntityType = "account";
export const ENTITY_TRANSACTION: EntityType = "transaction";
export const ENTITY_ASSET: EntityType = "asset";
export const ENTITY_SCHEDULE: EntityType = "schedule";
export const ENTITY_EVENT_TYPE: EntityType = "eventType";
export const ENTITY_BATTLE_PLAN: EntityType = "battlePlan";
export const ENTITY_BATTLE_PLAN_ITEM: EntityType = "battlePlanItem";

export const ACTIVITY_FIELDS: readonly (keyof ActivityInput & string)[] = [
  "shortName",
  "icon",
  "colorHex",
  "purpose",
];

export const LONG_TERM_GOAL_FIELDS: readonly (keyof LongTermGoalInput & string)[] = [
  "activityId",
  "title",
  "startAt",
  "deadlineAt",
  "progressMode",
  "progressPct",
  "targetMinorUnits",
  "targetCommodity",
  "accountId",
];

export const PLAN_FIELDS: readonly (keyof PlanInput & string)[] = [
  "ltgId",
  "title",
  "description",
  "startAt",
  "endAt",
];

export const TIMELINE_FIELDS: readonly (keyof TimelineInput & string)[] = [
  "birthAt",
  "projectedDeathAt",
  "timeZoneSegments",
];

export const EVENT_TYPE_FIELDS: readonly (keyof EventTypeInput & string)[] = [
  "name",
  "valueShape",
  "unit",
  "scaleLineageId",
];

export const EVENT_FIELDS: readonly (keyof EventInput & string)[] = [
  "at",
  "source",
  "eventTypeId",
  "body",
  "numericValue",
  "scaleOrder",
  "scaleSystemId",
  "activityId",
  "taskId",
  "pointId",
  "personIds",
  "geo",
  "targetType",
  "targetId",
];

export const PERSON_FIELDS: readonly (keyof PersonInput & string)[] = [
  "displayName",
  "photoRef",
  "description",
  "toneSystemId",
  "toneOrder",
];

export const COMMODITY_FIELDS: readonly (keyof CommodityInput & string)[] = [
  "symbol",
  "name",
  "kind",
  "precision",
];

export const CATEGORY_FIELDS: readonly (keyof CategoryInput & string)[] = [
  "name",
  "accountId",
  "parentId",
];

export const TAG_FIELDS: readonly (keyof TagInput & string)[] = ["name", "colorHex"];

export const ACCOUNT_FIELDS: readonly (keyof AccountInput & string)[] = [
  "name",
  "accountType",
  "commoditySymbol",
  "parentId",
  "activityId",
  "ltgId",
];

export const TRANSACTION_FIELDS: readonly (keyof TransactionInput & string)[] = [
  "status",
  "plannedAt",
  "actualAt",
  "memo",
  "activityId",
  "personId",
  "postings",
  "categoryId",
  "tagIds",
  "counterparty",
  "schedule",
  "settlesId",
];

export const ASSET_FIELDS: readonly (keyof AssetInput & string)[] = [
  "description",
  "category",
  "cost",
  "commoditySymbol",
  "acquiredAt",
  "activityId",
  "accountId",
];

export const SCHEDULE_FIELDS: readonly (keyof ScheduleInput & string)[] = [
  "activityId",
  "blocks",
];

export const BATTLE_PLAN_FIELDS: readonly (keyof BattlePlanInput & string)[] = [
  "kind",
  "periodKey",
];

export const BATTLE_PLAN_ITEM_FIELDS: readonly (keyof BattlePlanItemInput & string)[] = [
  "battlePlanId",
  "taskId",
  "sortKey",
  "slotStart",
  "slotEnd",
];

export const METRIC_FIELDS: readonly (keyof MetricInput & string)[] = [
  "activityId",
  "ltgId",
  "name",
  "cadence",
  "unit",
  "direction",
  "target",
  "min",
  "max",
];

export const POINT_FIELDS: readonly (keyof PointInput & string)[] = [
  "metricId",
  "at",
  "periodKey",
  "value",
  "conditionalSystemId",
  "conditionOrder",
  "note",
];

export const TASK_FIELDS: readonly (keyof TaskInput & string)[] = [
  "planId",
  "pointId",
  "parentTaskId",
  "sortKey",
  "status",
  "importance",
  "urgency",
  "title",
  "description",
  "plannedStartAt",
  "plannedEndAt",
  "deadlineAt",
  "actualStartAt",
  "actualEndAt",
];
